package com.bulut.mecitogullari.ui.screens.debts

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.nowEpochMillis
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.data.model.Supplier
import com.bulut.mecitogullari.data.model.balance
import com.bulut.mecitogullari.data.repository.DebtRepository
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

data class SupplierCardData(
    val supplier: Supplier,
    /** Positive = borç. */
    val balance: Double,
    val lastEntryEpochMillis: Long?,
)

data class DebtsUiState(
    val suppliers: List<SupplierCardData> = emptyList(),
    /** Open debt to suppliers. */
    val totalDebt: Double = 0.0,
    /** Open receivables from customers. */
    val totalReceivable: Double = 0.0,
)

class DebtsViewModel(
    private val debtRepository: DebtRepository,
) : ViewModel() {

    val uiState: StateFlow<DebtsUiState> = combine(
        debtRepository.suppliers,
        debtRepository.entries,
    ) { suppliers, entries ->
        val bySupplier = entries.groupBy { it.supplierId }
        val cards = suppliers.map { supplier ->
            val supplierEntries = bySupplier[supplier.id].orEmpty()
            SupplierCardData(
                supplier = supplier,
                balance = supplierEntries.balance(),
                lastEntryEpochMillis = supplierEntries.maxOfOrNull { it.dateEpochMillis },
            )
        }.sortedByDescending { it.balance }
        DebtsUiState(
            suppliers = cards,
            totalDebt = cards
                .filter { it.supplier.kind == AccountKind.SUPPLIER }
                .sumOf { it.balance.coerceAtLeast(0.0) },
            totalReceivable = cards
                .filter { it.supplier.kind == AccountKind.CUSTOMER }
                .sumOf { it.balance.coerceAtLeast(0.0) },
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = DebtsUiState(),
    )

    fun createAccount(kind: AccountKind, name: String, phone: String, notes: String) {
        if (name.isBlank()) return
        viewModelScope.launch {
            debtRepository.upsertSupplier(
                Supplier(
                    id = newId(),
                    name = name.trim(),
                    kind = kind,
                    phone = phone.trim(),
                    notes = notes.trim(),
                    createdAtEpochMillis = nowEpochMillis(),
                )
            )
        }
    }
}
